import {FormField, Separator, type FormControl} from 'platform/components';
import {Box, Heading, Text, VStack} from 'platform/foundation';

import {useWatch, type UseFormReturn} from 'react-hook-form';
import {Trans} from 'react-i18next';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleSelectionFilterFields} from '../../../components/VehicleSelectionFilterFields/VehicleSelectionFilterFields';
import {type CampaignFormValues} from '../schemas/campaignFormSchema';
import {getCampaignVehicleSelectionBinding} from '../utils/getCampaignVehicleSelectionBinding';
import {CampaignManuallySelectedVehicles} from './CampaignManuallySelectedVehicles';

interface CampaignVehicleSelectionSectionProps extends RequiredTestIdProps {
  control: FormControl<CampaignFormValues>;
  formApi: UseFormReturn<CampaignFormValues>;
}

export function CampaignVehicleSelectionSection(props: CampaignVehicleSelectionSectionProps) {
  const [filter, isAppliedToAllVehicles, manuallySelectedVehicles] = useWatch({
    control: props.control,
    name: ['filter', 'isAppliedToAllVehicles', 'manuallySelectedVehicles'],
  });

  return (
    <VStack spacing={6}>
      <Heading size={4}>{i18n.t('entity.salesCampaign.sections.vehicleSelection')}</Heading>

      <Box
        padding={4}
        borderRadius="medium"
        border="1px solid"
        borderColor="general.separator"
        data-testid={suffixTestId('filter', props)}
      >
        <VStack spacing={4}>
          <FormField
            control={props.control}
            type="checkbox"
            name="isAppliedToAllVehicles"
            label={i18n.t('entity.salesCampaign.labels.applyToAllVehicles')}
            helperText={i18n.t('entity.salesCampaign.hints.applyToAllVehicles')}
            data-testid={suffixTestId('filter-applyToAllVehicles', props)}
          />

          <Separator spacing={0} />

          <VStack spacing={1}>
            <Heading size={5}>{i18n.t('entity.interest.labels.conditions')}</Heading>
            <Text size="xSmall" color="tertiary">
              <Trans
                i18nKey="entity.salesCampaign.sections.conditionsDescription"
                components={{break: <br />}}
              />
            </Text>
          </VStack>

          <VehicleSelectionFilterFields
            control={props.control}
            binding={getCampaignVehicleSelectionBinding(props.formApi)}
            filter={filter}
            isDisabled={isAppliedToAllVehicles}
            data-testid={suffixTestId('filter', props)}
          />
        </VStack>
      </Box>
      <CampaignManuallySelectedVehicles
        value={manuallySelectedVehicles}
        onChange={(next) =>
          props.formApi.setValue('manuallySelectedVehicles', next, {shouldDirty: true})
        }
        data-testid={suffixTestId('manuallySelectedVehicles', props)}
      />
    </VStack>
  );
}
